import { TFunction } from "i18next";
import Papa from "papaparse";

import { detectFileTextEncoding, IBulkPrepareBooking } from "@ryde/shared";

import {
  getBulkUploadBookingColumnsMap,
  resolveBulkUploadBookingColumnKey,
} from "./bulk-upload-bookings-columns.helpers";

interface IBulkUploadBookingsCsvRow {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __parsed_extra?: string[];
  // eslint-disable-next-line @typescript-eslint/member-ordering
  [key: string]: unknown;
}

const normalizeBulkUploadBookingRow = ({
  row,
  index,
  columnsMap,
}: {
  row: IBulkUploadBookingsCsvRow;
  index: number;
  columnsMap: Map<string, string>;
}): IBulkPrepareBooking => {
  const { __parsed_extra: parsedExtra, ...restRow } = row;

  const transformedRow: Record<string, unknown> = {};
  const rowKeys = Object.keys(restRow);

  // Headers may arrive in the active language ("עובד") or with an inconsistent first-letter
  // case ("Shift" instead of "shift"), so resolve every key to the API field name.
  for (const rowKey of rowKeys) {
    const rowKeyTransformed = resolveBulkUploadBookingColumnKey({
      header: rowKey,
      columnsMap,
    });
    const rowValue = restRow[rowKey];

    transformedRow[rowKeyTransformed] = rowValue;
  }

  const transformedItem: IBulkPrepareBooking = {
    index: index + 1,
    ...transformedRow,
  };

  if (parsedExtra?.length) {
    transformedItem.extraFields = parsedExtra;
  }

  return transformedItem;
};

export const bulkUploadBookingsCsvFile = async ({
  file,
  t,
}: {
  file: File;
  t: TFunction;
}): Promise<Papa.ParseResult<IBulkPrepareBooking>> => {
  // papaparse defaults to UTF-8, which mangles the windows-1255 CSVs Excel produces in Hebrew.
  const encoding = await detectFileTextEncoding(file);
  const columnsMap = getBulkUploadBookingColumnsMap(t);

  return new Promise((resolve, reject) => {
    const onComplete = (
      result: Papa.ParseResult<IBulkUploadBookingsCsvRow>,
    ): void => {
      const transformedData: IBulkPrepareBooking[] = result.data.map(
        (row, index) =>
          normalizeBulkUploadBookingRow({ row, index, columnsMap }),
      );

      resolve({
        ...result,
        data: transformedData,
      });
    };

    Papa.parse<IBulkUploadBookingsCsvRow>(file, {
      worker: true,
      header: true,
      skipEmptyLines: true,
      encoding,
      error: err => reject(err),
      complete: onComplete,
    });
  });
};
